import { Bot, CreditCard, Repeat } from 'lucide-react'
import {
  SubtleTabs,
  SubtleTabsContent,
  SubtleTabsList,
  SubtleTabsTrigger,
} from '@/shared/components/common/tabs/SubtleTabs'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import type { Plan } from '../../services/plansService'
import type { EditableCharge } from '../../types/customerDetail.types'
import { ChargeTable } from './ChargeTable'

interface ChargesSectionProps {
  selectedPlan: Plan | null
  editableCharges: EditableCharge[]
  activeChargeTab: string
  setActiveChargeTab: (tab: string) => void
  onChargeUpdate: (chargeId: string, newPrice: number) => void
  onEditCharge?: (charge: EditableCharge) => void
}

export function ChargesSection({
  editableCharges,
  activeChargeTab,
  setActiveChargeTab,
  onChargeUpdate,
  onEditCharge,
}: ChargesSectionProps) {
  const fixedCharges = editableCharges.filter(c => c.category === 'fixed')
  const recurringCharges = editableCharges.filter(
    c => c.category === 'recurring'
  )
  const agentCharges = editableCharges.filter(c => c.category === 'agent')
  // DEPRECATED: const featureCharges = editableCharges.filter(c => c.category === 'feature')

  return (
    <Card className='bg-transparent border'>
      <CardHeader>
        <CardTitle>Charges</CardTitle>
      </CardHeader>
      <CardContent>
        <SubtleTabs
          value={activeChargeTab}
          onValueChange={setActiveChargeTab}
          className='w-full'
        >
          <SubtleTabsList className='grid w-full grid-cols-3'>
            <SubtleTabsTrigger
              value='fixed'
              className='flex items-center gap-2'
            >
              <CreditCard className='w-4 h-4' />
              Fixed Costs ({fixedCharges.length})
            </SubtleTabsTrigger>
            <SubtleTabsTrigger
              value='recurring'
              className='flex items-center gap-2'
            >
              <Repeat className='w-4 h-4' />
              Recurring ({recurringCharges.length})
            </SubtleTabsTrigger>
            <SubtleTabsTrigger
              value='agents'
              className='flex items-center gap-2'
            >
              <Bot className='w-4 h-4' />
              Agents ({agentCharges.length})
            </SubtleTabsTrigger>
            {/* DEPRECATED: Features tab hidden (see DEPRECATED.md)
            <SubtleTabsTrigger
              value='features'
              className='flex items-center gap-2'
            >
              <Zap className='w-4 h-4' />
              Features ({featureCharges.length})
            </SubtleTabsTrigger> */}
          </SubtleTabsList>

          <SubtleTabsContent value='fixed' className='mt-6'>
            {fixedCharges.length > 0 ? (
              <ChargeTable
                charges={fixedCharges}
                chargeType='fixed'
                onChargeUpdate={onChargeUpdate}
                onEditCharge={onEditCharge}
              />
            ) : (
              <div className='text-center py-12 text-muted-foreground'>
                <CreditCard className='w-12 h-12 mx-auto mb-4 opacity-50' />
                <p>No fixed costs defined for this plan</p>
              </div>
            )}
          </SubtleTabsContent>

          <SubtleTabsContent value='recurring' className='mt-6'>
            {recurringCharges.length > 0 ? (
              <ChargeTable
                charges={recurringCharges}
                chargeType='recurring'
                onChargeUpdate={onChargeUpdate}
                onEditCharge={onEditCharge}
              />
            ) : (
              <div className='text-center py-12 text-muted-foreground'>
                <Repeat className='w-12 h-12 mx-auto mb-4 opacity-50' />
                <p>No recurring charges defined for this plan</p>
              </div>
            )}
          </SubtleTabsContent>

          <SubtleTabsContent value='agents' className='mt-6'>
            {agentCharges.length > 0 ? (
              <ChargeTable
                charges={agentCharges}
                chargeType='agents'
                onChargeUpdate={onChargeUpdate}
                onEditCharge={onEditCharge}
              />
            ) : (
              <div className='text-center py-12 text-muted-foreground'>
                <Bot className='w-12 h-12 mx-auto mb-4 opacity-50' />
                <p>No agent charges defined for this plan</p>
              </div>
            )}
          </SubtleTabsContent>

          {/* DEPRECATED: Features tab content hidden (see DEPRECATED.md)
          <SubtleTabsContent value='features' className='mt-6'>
            {featureCharges.length > 0 ? (
              <ChargeTable
                charges={featureCharges}
                chargeType='features'
                onChargeUpdate={onChargeUpdate}
                onEditCharge={onEditCharge}
              />
            ) : (
              <div className='text-center py-12 text-muted-foreground'>
                <Zap className='w-12 h-12 mx-auto mb-4 opacity-50' />
                <p>No feature charges defined for this plan</p>
              </div>
            )}
          </SubtleTabsContent> */}
        </SubtleTabs>
      </CardContent>
    </Card>
  )
}
